import React from 'react';
import { cn } from '@/lib/utils';
import { format, isToday, isYesterday } from 'date-fns';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Badge } from '@/components/ui/badge';
import { ChatHistoryGroup } from '@/types/navigation';
import { MessageSquare, Clock, Edit } from 'lucide-react';

interface HistorySectionProps {
  chatHistory: ChatHistoryGroup[];
  onSelectConversation: (conversationId: string) => void;
  historyFilter: 'today' | 'yesterday' | 'week' | 'older';
  onFilterChange: (filter: 'today' | 'yesterday' | 'week' | 'older') => void;
}

const HistorySection: React.FC<HistorySectionProps> = ({
  chatHistory,
  onSelectConversation,
  historyFilter,
  onFilterChange
}) => {
  const filterButtons = [
    { key: 'today' as const, label: 'Today' },
    { key: 'yesterday' as const, label: 'Yesterday' },
    { key: 'week' as const, label: 'Week' },
    { key: 'older' as const, label: 'All' }
  ];

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    if (isToday(date)) return format(date, 'HH:mm');
    if (isYesterday(date)) return 'Yesterday';
    return format(date, 'MMM d');
  };

  if (chatHistory.length === 0) {
    return (
      <div className="space-y-2">
        {/* Filter buttons */}
        <div className="flex flex-wrap gap-1">
          {filterButtons.map(filter => (
            <Button
              key={filter.key}
              variant={historyFilter === filter.key ? "secondary" : "ghost"}
              size="sm"
              onClick={() => onFilterChange(filter.key)}
              className="h-6 px-2 text-xs"
            >
              {filter.label}
            </Button>
          ))}
        </div>
        
        {/* Empty state */}
        <div className="text-center py-4">
          <Clock className="h-8 w-8 mx-auto text-muted-foreground/50 mb-2" />
          <p className="text-xs text-muted-foreground">No conversations yet</p>
          <p className="text-xs text-muted-foreground/70">Start chatting to see history here</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {/* Filter buttons */}
      <div className="flex flex-wrap gap-1">
        {filterButtons.map(filter => (
          <Button
            key={filter.key}
            variant={historyFilter === filter.key ? "secondary" : "ghost"}
            size="sm"
            onClick={() => onFilterChange(filter.key)}
            className="h-6 px-2 text-xs"
          >
            {filter.label}
          </Button>
        ))}
      </div>

      {/* History groups */}
      <div className="space-y-3">
        {chatHistory.map(group => (
          <div key={group.date} className="space-y-1">
            {/* Group label */}
            <div className="text-xs font-medium text-muted-foreground/70 px-2">
              {group.label}
            </div>
            
            {/* Conversations */}
            <div className="space-y-0.5">
              {group.conversations.map(conversation => (
                <TooltipProvider key={conversation.id}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onSelectConversation(conversation.id)}
                        className={cn(
                          "w-full justify-start gap-2 px-2 py-2 h-auto text-xs font-normal",
                          "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                        )}
                      >
                        {conversation.sessionType === 'update' ? (
                          <Edit className="h-3 w-3 flex-shrink-0 text-blue-600" />
                        ) : (
                          <MessageSquare className="h-3 w-3 flex-shrink-0 text-green-600" />
                        )}
                        <div className="flex-1 min-w-0 text-left">
                          <div className="truncate font-medium">
                            {conversation.title.replace(/^Title:\s*/i, '')}
                          </div>
                        </div>
                        {conversation.message_count !== undefined && (
                          <Badge variant="secondary" className="text-xs px-1 py-0 h-4 ml-1">
                            {conversation.message_count}
                          </Badge>
                        )}
                      </Button>
                    </TooltipTrigger>
                    {conversation.short_summary && (
                      <TooltipContent side="right">
                        {conversation.short_summary}
                      </TooltipContent>
                    )}
                  </Tooltip>
                </TooltipProvider>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default HistorySection; 